import { LINK_FILTER_LABEL } from '@/lib/experience-labels';
import { EXPERIENCE_WORDING } from '@/lib/experience-wording';
import type {
  VersionedCounts,
  VersionedFilterChip,
  VersionedListSpec,
  VersionedSearch,
} from '@/lib/versioned-filters';
import {
  activeVersionedFilters,
  filterVersioned,
  hasActiveVersionedFilters,
  hasHiddenVersionedFilter,
  readVersionedFilters,
  resetVersionedFilters,
  versionedAuthors,
  versionedCountLabel,
  versionedCounts,
  versionedFiltersOf,
  versionedSearchOf,
} from '@/lib/versioned-filters';
import type { ExperienceCard, ExperienceFilters } from '@matter_extractor/shared';
import { EXPERIENCE_FILTERS_DEFAULT, experienceLinkFilterSchema } from '@matter_extractor/shared';

/**
 * What the Expériences list adds to the common rule set of §4 (refonte
 * modulaire §15).
 *
 * Thirteen of the filters, the four sorts, the sparse URL, the chips and the
 * counting are in `versioned-filters.ts`, and every versioned object gets them.
 * An experience adds exactly THREE dimensions, all of them filters:
 *
 *   - **protocole** and **persona** - « les deux références qu'une expérience
 *     porte », which is the other direction of the arrow the common referent
 *     filters point along;
 *   - **lien actif** - whether a link of §5.1 can still make somebody enter it.
 *
 * And **no sort of its own** (§15): none of the three orders anything - « les
 * expériences qui jouent Entretien fondateur » is a filter, not a ranking - and
 * a sort nobody asked for is a control everybody reads past.
 *
 * ## The two reference filters carry a NAME, not an id, and the field says Id
 *
 * `experienceFiltersSchema` spells them `referencedProtocolId` and
 * `referencedPersonaId`, and they are the right fields: they mean « the
 * reference this experience CARRIES », which is the opposite of the
 * `protocolId` of `versionedFiltersShape`. What travels in them here is the
 * protocol's or the persona's NAME, because `experienceCardSchema` (issue #54)
 * names both and carries neither id - the list is served whole and filtered in
 * the page, so a card is the only thing this module can compare against.
 *
 * The consequence is named rather than hidden: two protocols sharing a name -
 * §7.1 does not make a protocol's name unique, where §10.1 does for a persona -
 * are conflated by this filter. The alternative was a round trip per keystroke
 * or a card shape changed from a front lot, and the facet answers « lesquelles
 * jouent celui-ci » correctly in every case an operator meets. The day the card
 * carries the two ids, the option values become ids and this paragraph goes.
 *
 * ## « Lien actif » answers « non » for everybody, on purpose
 *
 * There are no links until the Partage lot (issue #59) posts the table, so no
 * experience has an active one and `linkCount` is 0 for all of them. The facet
 * is declared now - as `experienceFiltersSchema` declares its field - so the
 * filter bar does not change shape under the screen when the links land.
 */

/** Does this card survive the protocol facet? */
function matchesProtocol(card: ExperienceCard, filters: ExperienceFilters): boolean {
  if (filters.referencedProtocolId === null) return true;
  return card.protocolName === filters.referencedProtocolId;
}

/** Does this card survive the persona facet? */
function matchesPersona(card: ExperienceCard, filters: ExperienceFilters): boolean {
  if (filters.referencedPersonaId === null) return true;
  return card.personaName === filters.referencedPersonaId;
}

/** Does this card survive « Lien actif » (§15)? */
function matchesLink(card: ExperienceCard, filters: ExperienceFilters): boolean {
  switch (filters.activeLink) {
    case 'all':
      return true;
    case 'yes':
      return card.linkCount > 0;
    case 'no':
      return card.linkCount === 0;
  }
}

/** A non-empty string of a raw search value, or undefined - never throws. */
function readName(raw: unknown): string | undefined {
  return typeof raw === 'string' && raw !== '' ? raw : undefined;
}

export const EXPERIENCE_LIST_SPEC: VersionedListSpec<ExperienceCard, ExperienceFilters> = {
  defaults: EXPERIENCE_FILTERS_DEFAULT,

  read: (search) => {
    const patch: Partial<ExperienceFilters> = {};
    /*
      A name that no card carries SURVIVES into the filter rather than being
      dropped, the régime `persona-filters.ts` settled for its cran: this runs
      as `validateSearch`, before any query, so the list is not readable yet -
      and dropping every value that cannot be checked would drop the legitimate
      one too, on the first paint of a pasted link. The chip then words it
      honestly and the grid is empty rather than wrong.
    */
    const protocol = readName(search.referencedProtocolId);
    if (protocol !== undefined) patch.referencedProtocolId = protocol;
    const persona = readName(search.referencedPersonaId);
    if (persona !== undefined) patch.referencedPersonaId = persona;
    const link = experienceLinkFilterSchema.safeParse(search.activeLink);
    if (link.success && link.data !== EXPERIENCE_FILTERS_DEFAULT.activeLink) {
      patch.activeLink = link.data;
    }
    return patch;
  },

  write: (filters) => {
    const search: Partial<ExperienceFilters> = {};
    if (filters.referencedProtocolId !== null) {
      search.referencedProtocolId = filters.referencedProtocolId;
    }
    if (filters.referencedPersonaId !== null) {
      search.referencedPersonaId = filters.referencedPersonaId;
    }
    if (filters.activeLink !== EXPERIENCE_FILTERS_DEFAULT.activeLink) {
      search.activeLink = filters.activeLink;
    }
    return search;
  },

  matches: (card, filters) =>
    matchesProtocol(card, filters) && matchesPersona(card, filters) && matchesLink(card, filters),

  chips: (filters): VersionedFilterChip<ExperienceFilters>[] => {
    const chips: VersionedFilterChip<ExperienceFilters>[] = [];
    if (filters.referencedProtocolId !== null) {
      chips.push({
        id: 'referencedProtocolId',
        label: `Protocole : ${filters.referencedProtocolId}`,
        patch: { referencedProtocolId: null },
      });
    }
    if (filters.referencedPersonaId !== null) {
      chips.push({
        id: 'referencedPersonaId',
        label: `Persona : ${filters.referencedPersonaId}`,
        patch: { referencedPersonaId: null },
      });
    }
    if (filters.activeLink !== 'all') {
      chips.push({
        id: 'activeLink',
        label: `Lien actif : ${LINK_FILTER_LABEL[filters.activeLink].toLowerCase()}`,
        patch: { activeLink: 'all' },
      });
    }
    return chips;
  },

  /** None: the four common sorts are all an experience orders by (§15). */
  sorts: {},
};

/* -------------------------------------------------- the layer, bound to §9 */

export type ExperienceSearch = VersionedSearch<ExperienceFilters>;
export type { VersionedCounts as ExperienceCounts };

export const readExperienceFilters = (search: Record<string, unknown>): ExperienceSearch =>
  readVersionedFilters(search, EXPERIENCE_LIST_SPEC);

export const experienceFiltersOf = (search: ExperienceSearch): ExperienceFilters =>
  versionedFiltersOf(search, EXPERIENCE_LIST_SPEC);

export const experienceSearchOf = (filters: ExperienceFilters): ExperienceSearch =>
  versionedSearchOf(filters, EXPERIENCE_LIST_SPEC);

export const filterExperiences = (
  cards: readonly ExperienceCard[],
  filters: ExperienceFilters,
): ExperienceCard[] => filterVersioned(cards, filters, EXPERIENCE_LIST_SPEC);

export const experienceCounts = (
  cards: readonly ExperienceCard[],
  filters: ExperienceFilters,
): VersionedCounts => versionedCounts(cards, filters, EXPERIENCE_LIST_SPEC);

/** « 6 expériences » with nothing filtered, « 2 expériences sur 6 » with something (§4). */
export const experienceCountLabel = (counts: VersionedCounts): string =>
  versionedCountLabel(counts, EXPERIENCE_WORDING.noun);

export const experienceAuthors = versionedAuthors;

export const activeExperienceFilters = (
  filters: ExperienceFilters,
): VersionedFilterChip<ExperienceFilters>[] =>
  activeVersionedFilters(filters, EXPERIENCE_LIST_SPEC);

export const hasActiveExperienceFilters = (filters: ExperienceFilters): boolean =>
  hasActiveVersionedFilters(filters, EXPERIENCE_LIST_SPEC);

export const hasHiddenExperienceFilter = (filters: ExperienceFilters): boolean =>
  hasHiddenVersionedFilter(filters, EXPERIENCE_LIST_SPEC);

export const resetExperienceFilters = (filters: ExperienceFilters): ExperienceFilters =>
  resetVersionedFilters(filters, EXPERIENCE_LIST_SPEC);

/**
 * The distinct protocol and persona names the library actually holds, sorted -
 * what the two selects of the visible row offer (§15).
 *
 * Derived from the CARDS and not from the two libraries: a select listing every
 * protocol of the base would offer dozens of names no experience plays, and
 * each of them would empty the grid. The names come back sorted so two runs of
 * the same list draw the same select.
 */
export function experienceReferenceOptions(cards: readonly ExperienceCard[]): {
  protocols: string[];
  personas: string[];
} {
  const protocols = new Set<string>();
  const personas = new Set<string>();
  for (const card of cards) {
    if (card.protocolName !== null) protocols.add(card.protocolName);
    if (card.personaName !== null) personas.add(card.personaName);
  }
  return {
    protocols: [...protocols].sort((a, b) => a.localeCompare(b, 'fr')),
    personas: [...personas].sort((a, b) => a.localeCompare(b, 'fr')),
  };
}
